import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { IVehicle } from 'app/shared/model/vehicle.model';
import { getEntities as getVehicles } from 'app/entities/vehicle/vehicle.reducer';
import { IDriver } from 'app/shared/model/driver.model';
import { getEntities as getDrivers } from 'app/entities/driver/driver.reducer';
import { ITourSchedule } from 'app/shared/model/tour-schedule.model';
import { getEntity, updateEntity, createEntity, reset } from './tour-schedule.reducer';

export const TourScheduleUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tours = useAppSelector(state => state.tour.entities);
  const vehicles = useAppSelector(state => state.vehicle.entities);
  const drivers = useAppSelector(state => state.driver.entities);
  const tourScheduleEntity = useAppSelector(state => state.tourSchedule.entity);
  const loading = useAppSelector(state => state.tourSchedule.loading);
  const updating = useAppSelector(state => state.tourSchedule.updating);
  const updateSuccess = useAppSelector(state => state.tourSchedule.updateSuccess);

  const [formData, setFormData] = useState<any>({
    createdBy: account?.id,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/tour-schedule' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTours({}));
    dispatch(getVehicles({}));
    dispatch(getDrivers({}));
  }, []);

  useEffect(() => {
    if (tourScheduleEntity) {
      setFormData({
        ...tourScheduleEntity,
        createdBy: tourScheduleEntity?.createdBy?.id,
        tour: tourScheduleEntity?.tour?.id,
        vehicle: tourScheduleEntity?.vehicle?.id,
        driver: tourScheduleEntity?.driver?.id,
      });
    }
  }, [tourScheduleEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    if (!formData.tour) {
      newErrors.tour = 'Tour is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.noPassengers !== undefined && typeof values.noPassengers !== 'number') {
      values.noPassengers = Number(values.noPassengers);
    }
    if (values.noKids !== undefined && typeof values.noKids !== 'number') {
      values.noKids = Number(values.noKids);
    }
    if (values.noPets !== undefined && typeof values.noPets !== 'number') {
      values.noPets = Number(values.noPets);
    }

    values.vehicle = values.vehicle || null;
    values.driver = values.driver || null;

    const entity = {
      ...tourScheduleEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      tour: tours.find(it => it.id.toString() === values.tour?.toString()),
      vehicle: vehicles.find(it => it.id.toString() === values.vehicle?.toString()),
      driver: drivers.find(it => it.id.toString() === values.driver?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.home.createOrEditLabel">Create or edit a TourSchedule</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField
                id="tour-schedule-id"
                name="id"
                label={translate('global.field.id')}
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourSchedule.code')}
              id="tour-schedule-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourSchedule.startDatetime')}
              id="tour-schedule-startDatetime"
              name="startDatetime"
              data-cy="startDatetime"
              value={formData.startDatetime || ''}
              onChange={handleChange('startDatetime')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourSchedule.noPassengers')}
              id="tour-schedule-noPassengers"
              name="noPassengers"
              data-cy="noPassengers"
              value={formData.noPassengers || ''}
              onChange={handleChange('noPassengers')}
              fullWidth
              type="number"
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourSchedule.noKids')}
              id="tour-schedule-noKids"
              name="noKids"
              data-cy="noKids"
              value={formData.noKids || ''}
              onChange={handleChange('noKids')}
              fullWidth
              type="number"
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourSchedule.noPets')}
              id="tour-schedule-noPets"
              name="noPets"
              data-cy="noPets"
              value={formData.noPets || ''}
              onChange={handleChange('noPets')}
              fullWidth
              type="number"
            />
            <FormControl fullWidth required error={!!errors.tour}>
              <InputLabel id="tour-schedule-tour-label">{translate('jhipsterSampleApplicationApp.tourSchedule.tour')}</InputLabel>
              <Select
                labelId="tour-schedule-tour-label"
                id="tour-schedule-tour"
                name="tour"
                data-cy="tour"
                value={formData.tour || ''}
                label={translate('jhipsterSampleApplicationApp.tourSchedule.tour')}
                onChange={handleSelectChange('tour')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tours
                  ? tours.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tour-schedule-vehicle-label">{translate('jhipsterSampleApplicationApp.tourSchedule.vehicle')}</InputLabel>
              <Select
                labelId="tour-schedule-vehicle-label"
                id="tour-schedule-vehicle"
                name="vehicle"
                data-cy="vehicle"
                value={formData.vehicle || ''}
                label={translate('jhipsterSampleApplicationApp.tourSchedule.vehicle')}
                onChange={handleSelectChange('vehicle')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {vehicles
                  ? vehicles.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.plate}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tour-schedule-driver-label">{translate('jhipsterSampleApplicationApp.tourSchedule.driver')}</InputLabel>
              <Select
                labelId="tour-schedule-driver-label"
                id="tour-schedule-driver"
                name="driver"
                data-cy="driver"
                value={formData.driver || ''}
                label={translate('jhipsterSampleApplicationApp.tourSchedule.driver')}
                onChange={handleSelectChange('driver')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {drivers
                  ? drivers.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.name}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/tour-schedule"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TourScheduleUpdate;
